define((require) => {
    "use strict";

    const Class           = require("shared/util/Class");
    const $               = require("jquery");
    const Color           = require("shared/util/Color");
    const EditorEvents    = require("shared/services/EditorEvents");
    const KeyCode         = require("shared/services/KeyCode");

    const DEFAULT_COLOR = "#000000";
    const COLOR_LIBRARY_HEX = [
        ["#000000", "#626262", "#9b9b9b", "#dbdbdb", "#eeeeee", "#ffffff", "transparent"],    // row 1
        ["#5d393f", "#4f483f", "#4d523f", "#20523f", "#20393f", "#203958", "#5d395e"],        // row 2
        ["#b9615a", "#c19d32", "#bfc200", "#00ab57", "#3e92c8", "#4463bd", "#b961af"],        // row 3
        ["#ff615a", "#ffc32c", "#ffff00", "#43d76d", "#50cdff", "#5f89ff", "#ff61e3"],        // row 4
        ["#ffa59a", "#ffd496", "#f8ee87", "#92ea92", "#a4e5f4", "#bfdcff", "#f2ceda"],        // row 5
        ["#ffefe7", "#fff7d8", "#ffffc4", "#d9ffde", "#e3fbff", "#e3ecff", "#fceeff"]         // row 6
    ];

    return Class.extend({
        _color: null,
        _input: null,
        _colorBox: null,
        _library: null,
        _includeTransparency: false,

        _trackingName: null,
        _trackHexKeyUp: false,
        _trackColorLibraryClick: false,

        constructor(args) {
            this._color                 = args.color;
            this._input                 = args.input;
            this._colorBox              = args.colorBox;
            this._includeTransparency   = !!args.includeTransparency;

            this._setupColorpicker(args.color);

            if (args.library) {
                this._library = args.library;
                COLOR_LIBRARY_HEX.forEach(this._addRowOfColorSquaresToLibrary.bind(this));
            }

            // Set up listeners
            this._eventListeners();

            if (args.tracking) {
                this._trackingName = args.tracking;
                this._setUpTracking();
            }
        },

        // Sets up event listeners
        _eventListeners() {
            this._input
                .on("changeColor", this._colorpickerHandler.bind(this))
                .on("keyup", this._keyupEventHandler.bind(this));

            this._colorBox.on("click", this._colorBoxHandler.bind(this));

            if (this._library) {
                this._library.on("click", this._colorLibraryHandler.bind(this));
            }
        },

        // HANDLERS

        // ColorPicker handler
        _colorpickerHandler(event) {
            const color = event.color.toHex();

            this._input.val(color).trigger("change");
            this._updateColorBox(color);
            this._updateSelectedLibraryColor();
        },

        // Input field handler
        _keyupEventHandler(event) {
            const color = event.target.value;

            switch (event.keyCode) {
                case KeyCode.LEFT:
                case KeyCode.RIGHT:
                case KeyCode.UP:
                case KeyCode.DOWN:
                    return;
                default:
                    if (this._isValidHex(color)) {
                        this._triggerChange();
                        this._updateColorBox(color);
                    }
                    this._updateSelectedLibraryColor();
            }
        },

        // Color box handler (the tiny box next to the input field)
        _colorBoxHandler() {
            this._input.trigger("focus");
        },

        // Clicking on the colored squares in the library (if it's included)
        _colorLibraryHandler(event) {
            const target = $(event.target);
            const color = (target.data("transparent")) ? "transparent" : Color.rgbaToHex(target.css("background-color")).hex;

            if (target.prop("tagName") !== "SPAN") return;

            if (this._isTransparent(color)) {
                this._input.val("");
            } else {
                this._input.colorpicker("setValue", color);
            }
            this._triggerChange();
            this._updateColorBox(color);

            // update selected state in ui
            this._updateSelectedLibraryColor(target);
        },


        // Set up the color picker with the right color from the model
        _setupColorpicker(color) {
            if (color) {
                this._input.colorpicker("setValue", color);
            } else {
                this._input.colorpicker();
            }
            this._updateColorBox(color);
        },


        // Used by the library to create the squares
        _addRowOfColorSquaresToLibrary(arrayOfHexes) {
            const row = $("<div>", {"class": "color-row"});
            arrayOfHexes.forEach((hex) => {
                if (this._includeTransparency || !this._isTransparent(hex)) {
                    row.append(this._createColorSquare(hex));
                }
            });
            this._library.append(row);
        },

        _createColorSquare(color) {
            const attributes = (this._isTransparent(color)) ? {"data-transparent": true} : {css: {backgroundColor: color}};
            const isSelected = (color === this._color) || (color === "transparent" && !this._color);
            return $("<span>", attributes).toggleClass("selected", isSelected);
        },

        dispose() {
            this._input.colorpicker("dispose").off();
            this._colorBox.off();
            if (this._library) this._library.off();
            this._sendTracking();
        },

        // Helper function
        _isValidHex(hex) {
            return (/(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i).test(hex);
        },

        // Takes a "maybe hex" and returns a real hex or its default color
        hexOrDefault(hex) {
            if (hex === "" && this._includeTransparency) return "";

            if ((/(^#[0-9A-F]{6}$)/i).test(hex)) {
                return hex;
            } else if ((/(^#[0-9A-F]{3}$)/i).test(hex)) {
                // 3 digit to 6diget (#f0a -> #ff00aa)
                return hex[0] + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
            }

            return DEFAULT_COLOR;
        },

        // Saves to model and updates the actual widget
        _triggerChange() {
            this._input.trigger("change");
        },

        // Checks if a color is transparent
        _isTransparent(color) {
            return color === "transparent" || color === "";
        },

        // Helper function
        _updateSelectedLibraryColor(element) {
            if (this._library) {
                this._library.find(".selected").removeClass("selected");
                if (element) element.addClass("selected");
            }
        },

        // Updates the color (and class) of the little colored box next to the input field
        _updateColorBox(color) {
            if (this._isTransparent(color)) {
                this._colorBox.addClass("transparent").css("backgroundColor", color);
            } else {
                this._colorBox.removeClass("transparent").css("backgroundColor", this.hexOrDefault(color));
            }
        },


        // TRACKING

        _setUpTracking() {
            this._input
                .on("keyup", () => { this._trackHexKeyUp = true; });

            this._library
                .on("click", () => { this._trackColorLibraryClick = true; });
        },

        // Fire events for tracking with segment.io
        _sendTracking() {
            if (this._trackHexKeyUp)            EditorEvents.trigger(EditorEvents.constants.SELECT_COLOR, {triggerOrigin: "customColorInputField"});
            if (this._trackColorLibraryClick)   EditorEvents.trigger(EditorEvents.constants.SELECT_COLOR, {triggerOrigin: "colorPickerGridLibrary"});
        }
    }, {
        DEFAULT_COLOR
    });
});



// WEBPACK FOOTER //
// ./src/shared/services/ColorPicker.js